import { MONEDAS, type Moneda } from "@tora/shared";
import { cn } from "@/lib/utils";
import { useMoneda } from "./moneda-context";

const SIMBOLO: Record<Moneda, string> = { ARS: "$", USD: "US$" };

export interface SelectorMonedaProps {
  valor: Moneda;
  onChange: (m: Moneda) => void;
  /** `compacto` para ponerlo al lado de un input. */
  tamano?: "compacto" | "normal";
  disabled?: boolean;
  className?: string;
  /** Etiqueta accesible del grupo. */
  etiqueta?: string;
}

/**
 * Control segmentado ARS | USD, controlado.
 *
 * Dos botones y no un <select>: con solo dos opciones, un desplegable esconde el
 * estado actual detras de un clic. Aca la moneda activa se ve siempre, y ademas
 * se lee sin color (el boton activo tiene fondo solido y `aria-pressed`).
 */
export function SelectorMoneda({
  valor,
  onChange,
  tamano = "normal",
  disabled = false,
  className,
  etiqueta = "Moneda",
}: SelectorMonedaProps): JSX.Element {
  const compacto = tamano === "compacto";

  return (
    <div
      role="group"
      aria-label={etiqueta}
      className={cn(
        "inline-flex shrink-0 items-center gap-0.5 rounded-md border border-input bg-muted p-0.5",
        className,
      )}
    >
      {MONEDAS.map((m) => {
        const activo = m === valor;
        return (
          <button
            key={m}
            type="button"
            disabled={disabled}
            aria-pressed={activo}
            onClick={() => onChange(m)}
            className={cn(
              "inline-flex cursor-pointer items-center justify-center gap-1 rounded-sm font-semibold tabular-nums transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
              compacto ? "h-9 px-2 text-xs sm:h-8" : "h-10 px-3 text-sm sm:h-9",
              activo && m === "ARS" && "bg-ars text-ars-foreground",
              activo && m === "USD" && "bg-usd text-usd-foreground",
              !activo && "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
            )}
          >
            <span aria-hidden="true" className="opacity-80">
              {SIMBOLO[m]}
            </span>
            <span>{m}</span>
          </button>
        );
      })}
    </div>
  );
}

export interface SwitchMonedaProps {
  className?: string;
  /** Muestra la palabra "Ver en" adelante. Se oculta en mobile. */
  conEtiqueta?: boolean;
}

/**
 * Switch GLOBAL de moneda de visualizacion (ARS <-> USD).
 *
 * Cambia como se LEE el sistema, nunca lo que esta guardado: cada movimiento
 * persiste su tripleta (importe, moneda, cotizacion congelada) y este switch solo
 * elige con cual de las dos expresiones se muestra.
 */
export function SwitchMoneda({ className, conEtiqueta = true }: SwitchMonedaProps): JSX.Element {
  const { moneda, setMoneda } = useMoneda();

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {conEtiqueta ? (
        <span className="hidden text-xs font-medium uppercase tracking-wide text-muted-foreground lg:inline">
          Ver en
        </span>
      ) : null}
      <SelectorMoneda
        valor={moneda}
        onChange={setMoneda}
        etiqueta="Moneda de visualizacion"
        tamano="compacto"
      />
    </div>
  );
}
